/**
 * History endpoints and the adapter from stored rows to chat messages.
 *
 * GET history/conversation/{id}?include_images=false returns rows with `answer` (history contract, 3a):
 *   v2 rows: {protocol:2, text, sources, cited_ids, notice, mode, status}
 *   legacy rows: adapted server-side to protocol 1 (sources from the stored manual pages).
 * Rows without `answer` (an older backend) are adapted here from AssistantResponse/manuals.
 * Paging: `before=<message_id>` + `limit`; the response carries `has_more` and `conversation` (long-chat info).
 */
import axios from 'axios';
import { apiUrl, pageImagePath } from './config';
import { createAssistantMessage, indexSources } from '../state/chatReducer';

const userEmail = () => (typeof localStorage !== 'undefined' ? localStorage.getItem('userinfo') : null);

/** "BP_Volume8_Electrical_00DHT0028S8" -> "Vol.8 Electrical" (display fallback for legacy pages). */
export function shortManualLabel(name) {
  const s = String(name || '');
  const m = s.match(/Volume[_ ]?(\d+)[_ ]?([A-Za-z]+)?/i);
  if (!m) return s.replace(/_/g, ' ');
  // same model names as the backend's manual_label (core/sources.py)
  const prefix = /^eCANTER/i.test(s) ? 'eCanter ' : /^24TF1/.test(s) ? 'Canter MY24 ' : /^26MY/.test(s) ? 'Canter MY26 ' : '';
  const rest = m[2] && m[2].length >= 4 && m[2] === m[2].toUpperCase()
    ? m[2][0] + m[2].slice(1).toLowerCase() : m[2];
  return `${prefix}Vol.${m[1]}${rest ? ` ${rest}` : ''}`;
}

function parseAssistantResponse(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return { response: '' };
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' ? value : { response: String(value) };
  } catch (e) {
    return { response: raw };
  }
}

/** Old answers were stored with escaped newlines and inline image markdown. */
function normalizeLegacyText(text) {
  return String(text || '')
    .replace(/\\\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '');
}

function legacySources(row) {
  const manuals = Array.isArray(row.manuals) ? row.manuals : [];
  return manuals
    .filter((m) => m && (m.manual_name || m.base64_image))
    .map((m, i) => {
      const page = Number(m.page_number) || 0;
      const source = {
        id: i + 1,
        page_key: `${m.manual_name}#${page}`,
        manual_name: m.manual_name || '',
        manual_label: shortManualLabel(m.manual_name),
        page_number: page,
        page_label: `p.${page + 1}`,
        printed_page: null,
        section: m.section || '',
        section_title: '',
        title: '',
        snippet: '',
        model: m.model || '',
        year: '',
        figure: true,
        image_path: m.manual_name ? pageImagePath(m.manual_name, page) : '',
        thumb_path: m.manual_name ? pageImagePath(m.manual_name, page, 360) : '',
        score: Number(m.score) || 0,
      };
      if (m.base64_image) source.image_url = `data:image/jpeg;base64,${m.base64_image}`;
      return source;
    });
}

/** The `answer` of a row: as sent by the backend, from a stored v2 JSON, or adapted from a legacy row. */
export function answerOfRow(row) {
  if (row && row.answer && typeof row.answer === 'object') return row.answer;
  const parsed = parseAssistantResponse(row && row.AssistantResponse);
  if (parsed.v === 2 || parsed.protocol === 2) {
    return {
      protocol: 2,
      text: parsed.response || parsed.text || '',
      sources: parsed.sources || [],
      cited_ids: parsed.cited_ids || [],
      notice: parsed.notice || null,
      mode: parsed.mode,
      status: parsed.status || 'complete',
    };
  }
  return {
    protocol: 1,
    text: normalizeLegacyText(parsed.response || parsed.text || (typeof row.AssistantResponse === 'string' ? row.AssistantResponse : '')),
    sources: legacySources(row || {}),
    cited_ids: [],
    notice: null,
    mode: undefined,
    status: 'complete',
  };
}

function rowKey(row, index) {
  return row.message_id || row.RowKey || `${row.ChatId || 'c'}-${index}`;
}

/**
 * Rows (oldest first) -> [user, assistant, ...] messages. Follow-ups are kept only on the last row.
 * @param {Object[]} rows
 * @param {{followupsOnLast?: boolean}} [options]
 */
export function adaptHistoryRows(rows, { followupsOnLast = true } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  return list.flatMap((row, i) => {
    const key = rowKey(row, i);
    const answer = answerOfRow(row);
    const sources = Array.isArray(answer.sources) ? answer.sources : [];
    const cited = Array.isArray(answer.cited_ids) ? answer.cited_ids : [];
    const protocol = Number(answer.protocol) || 1;
    const isLast = i === list.length - 1;
    const suggestions = followupsOnLast && isLast && Array.isArray(row.suggestions) ? row.suggestions : [];
    return [
      { id: `h-u-${key}`, role: 'user', text: row.UserResponse || '' },
      createAssistantMessage({
        id: `h-a-${key}`,
        messageId: row.message_id || '',
        text: answer.text || '',
        status: answer.status === 'stopped' ? 'stopped' : 'complete',
        sources,
        sourceById: indexSources(sources),
        sourcesReceived: true,
        citedIds: cited,
        grounded: protocol >= 2 ? cited.length > 0 : null,
        notice: answer.notice || null,
        mode: answer.mode || null,
        protocol,
        followups: suggestions,
        followupsPrompt: (suggestions.find((s) => s && s.prompt) || {}).prompt || null,
        question: row.UserResponse || '',
        createdAt: row.Timestamp || null,
        finishReason: answer.status === 'stopped' ? 'stopped' : 'stop',
      }),
    ];
  });
}

/**
 * Fetch one conversation (lightweight: no base64 images).
 * @returns {Promise<{rows: Object[], messages: Object[], conversationInfo: ?Object, hasMore: boolean,
 *                     nextBefore: ?string, chatId: string, model: ?string, year: ?string}>}
 */
export async function fetchConversation(chatId, { before, limit, lang } = {}) {
  const params = { userEmail: userEmail(), include_images: false };
  if (lang) params.lang = lang;
  if (before) params.before = before;
  if (limit) params.limit = limit;
  const res = await axios.get(apiUrl(`history/conversation/${encodeURIComponent(chatId)}`), { params });
  const data = res.data || {};
  if (!data.valid && !(Array.isArray(data.messages) && data.messages.length)) {
    const err = new Error(data.message || 'Conversation not found');
    err.notFound = true;
    throw err;
  }
  const rows = Array.isArray(data.messages) ? data.messages : [];
  const last = rows[rows.length - 1] || {};
  const hasMore = Boolean(data.has_more);
  return {
    rows,
    messages: adaptHistoryRows(rows, { followupsOnLast: !before }),
    conversationInfo: data.conversation && typeof data.conversation === 'object' ? data.conversation : null,
    hasMore,
    // older messages are paged with ?before=<message_id of the oldest loaded row>
    nextBefore: data.next_before || (hasMore && rows[0] ? (rows[0].message_id || null) : null),
    chatId: last.ChatId || chatId,
    model: last.Model || last.model || null,
    year: last.Year || last.year || null,
  };
}

// The backend stores the index-side model name ("SuperGreat"); the pickers and the optimistic sidebar
// update use the display name ("Super Great"). Show one spelling.
const MODEL_LABELS = { supergreat: 'Super Great', canter: 'Canter', ecanter: 'eCanter' };
export const modelLabel = (model) => (model ? MODEL_LABELS[String(model).replace(/[\s_-]+/g, '').toLowerCase()] || model : null);

/** Normalized sidebar item. */
export function normalizeConversation(c) {
  return {
    id: c.chat_id || c.ChatId || c.id,
    title: c.title || c.UserResponse || '',
    createdAt: c.created_at || c.Timestamp || null,
    updatedAt: c.updated_at || c.created_at || c.Timestamp || null,
    model: modelLabel(c.model || c.Model || null),
    year: c.year || c.Year || null,
    preview: c.last_preview || c.preview || '',
    turns: c.message_count || c.turns || null,
  };
}

/**
 * One page of the conversation list. Uses the cursor (`before=<next_cursor>`) when the backend returns
 * one, else page numbers (older backends).
 * @returns {Promise<{items: Object[], hasMore: boolean, nextCursor: ?string}>}
 */
export async function fetchConversations({ page = 1, cursor = null, limit = 20 } = {}) {
  const params = { userEmail: userEmail(), limit };
  if (cursor) params.before = cursor;
  else params.page = page;
  const res = await axios.get(apiUrl('history/conversations'), { params });
  const data = res.data || {};
  const list = Array.isArray(data.conversations) ? data.conversations : [];
  const hasMore = typeof data.has_more === 'boolean'
    ? data.has_more
    : list.length >= limit && data.message !== 'No more conversations found';
  return {
    items: list.map(normalizeConversation).filter((c) => c.id),
    hasMore,
    nextCursor: data.next_cursor || null,
  };
}

export async function deleteConversation(chatId) {
  const res = await axios.delete(apiUrl('history'), {
    params: { ChatId: chatId, userEmail: userEmail() },
  });
  if (!res.data || !res.data.valid) throw new Error((res.data && res.data.message) || 'Delete failed');
  return true;
}
